import { ApolloQueryResult, FetchResult, gql, QueryHookOptions, QueryResult } from '@apollo/client';
import {
  VerificationMethodStatus,
  verificationMethodQuery,
  verificationMethodCreateMutation,
  VerificationMethodCreateInput,
  verificationMethodCreateMutationVariables,
  verificationMethodsQuery,
  verificationMethodsQueryVariables,
  VerificationMethodType,
  verificationMethodUpdateMutation,
  VerificationMethodUpdateInput,
  verificationMethodUpdateMutationVariables,
  verificationMethodQueryVariables,
  Org,
} from './verid-api-types';
import { useVeridApi } from './hybrid-apollo-client';
import { useAuthService } from './use-auth-service';
import { ORG_FIELDS } from './use-org-service';
import { useRef } from 'react';

export const VERIFICATION_METHOD_FIELDS = gql`
  fragment VerificationMethod on VerificationMethod {
    id
    createdAt
    updatedAt
    name
    description
    orgId
    type
    status
    requiresPicture
    instructions
  }
`;
const ID_TYPES_FIELDS = gql`
  fragment IdTypes on IdType {
    id
    description
    label
    placeHolder
    format
    useExpiryDate
    expiryDateFormat
    expiryDateLabel
    expiryDatePlaceHolder
  }
`;

export function useVerificationMethodService() {
  const api = useVeridApi();
  const authService = useAuthService();
  const verificationMethodsQuery = gql`
    ${VERIFICATION_METHOD_FIELDS}
    ${ID_TYPES_FIELDS}
    query verificationMethods(
      $query: VerificationMethodSearchInput!
      $after: String
      $before: String
      $first: Int
      $last: Int
      $skip: Int
      $orderBy: VerificationMethodOrder
    ) {
      verificationMethods(query: $query, after: $after, before: $before, first: $first, last: $last, skip: $skip, orderBy: $orderBy) {
        edges {
          node {
            ...VerificationMethod
            idTypes {
              ...IdTypes
            }
          }
        }
        totalCount
      }
    }
  `;

  const apiFunctions = useRef({
    useVerificationMethodsQuery(options: QueryHookOptions<verificationMethodsQuery, verificationMethodsQueryVariables> | undefined) {
      function fct(): QueryResult<verificationMethodsQuery, verificationMethodsQueryVariables> {
        return api.useQuery(verificationMethodsQuery, options);
      }
      return fct;
    },

    async verificationMethod(id: string): Promise<ApolloQueryResult<verificationMethodQuery>> {
      return api.query<verificationMethodQuery, verificationMethodQueryVariables>({
        query: gql`
          ${VERIFICATION_METHOD_FIELDS}
          ${ORG_FIELDS}
          query verificationMethod($id: String!) {
            verificationMethod(id: $id) {
              ...VerificationMethod
              org {
                ...Org
              }
            }
          }
        `,
        variables: { id },
        fetchPolicy: 'network-only',
      });
    },

    async verificationMethodCreate(
      verificationMethod: VerificationMethodCreateInput
    ): Promise<FetchResult<verificationMethodCreateMutation>> {
      const retVal = await api.mutate<verificationMethodCreateMutation, verificationMethodCreateMutationVariables>({
        mutation: gql`
          mutation verificationMethodCreate($data: VerificationMethodCreateInput!) {
            verificationMethodCreate(data: $data) {
              id
            }
          }
        `,
        variables: { data: verificationMethod },
        refetchQueries: ['verificationMethods'],
      });
      return retVal;
    },

    async verificationMethodUpdate(
      orgId: string,
      org: VerificationMethodUpdateInput
    ): Promise<FetchResult<verificationMethodUpdateMutation>> {
      return api.mutate<verificationMethodUpdateMutation, verificationMethodUpdateMutationVariables>({
        mutation: gql`
          mutation verificationMethodUpdate($id: String!, $data: VerificationMethodUpdateInput!) {
            verificationMethodUpdate(id: $id, data: $data) {
              id
            }
          }
        `,
        variables: { data: org, id: orgId },
        refetchQueries: ['verificationMethods'],
      });
    },

    async verificationMethods(data: verificationMethodsQueryVariables): Promise<ApolloQueryResult<verificationMethodsQuery>> {
      return api.query({
        query: verificationMethodsQuery,
        variables: {
          query: data.query,
          after: data.after,
          before: data.before,
          first: data.first,
          last: data.last,
          skip: data.skip,
          orderBy: data.orderBy,
        },
      });
    },
  });
  return apiFunctions.current;
}
